import { type World } from 'koota';
import { getContext } from '../context';
import { raycastClosest } from '../physics/queries';
import { Dead, DropState, Loose, RoundPhase, RoundState, Transform, Weapon } from '../traits';
import { weaponByIndex } from '../weapons/defs';
import { partyWeaponPool } from './party';
import { PartyState } from '../traits';
import { spawnWeapon } from '../weapons/systems';
import { navFor, routeStep, surfaceBelow } from '../ai/nav';

/** Ticks between the guns of the opening volley: a quick sweep across the arena, not one big clatter. */
const WAVE_GAP_TICKS = 8;

/** Every fighter gets an opening gun, while the ordinary four-player loose-gun budget stays intact. */
function looseWeaponLimit(world: World): number {
  const ctx = getContext(world);
  return Math.max(ctx.tuning.maxLooseWeapons, ctx.players.length);
}

/**
 * Called as "FIGHT" flashes. Nothing falls during the countdown (a gun landing beside one fighter
 * before anyone can move is a free win), and the first thing to fall is a volley of one gun per
 * fighter, aimed at nearby reachable ground rather than empty slices of the arena.
 */
export function openDrops(world: World): void {
  const ctx = getContext(world);
  const drop = world.get(DropState);
  if (!drop) return;
  const size = Math.min(ctx.players.length, looseWeaponLimit(world));
  world.set(DropState, {
    ...drop,
    nextDrop: ctx.tick + ctx.tuning.firstDropDelayTicks,
    wave: size,
    waveSize: size,
  });
}

/** Opening guns should land near a fighter on reachable ground, not collect on an overhead mover. */
function openingDropX(world: World, index: number, y: number): number | null {
  const ctx = getContext(world);
  const living = ctx.players.filter((player) => !player.has(Dead));
  const intended = ctx.players[index];
  const player = intended && !intended.has(Dead) ? intended : living[index % living.length];
  const position = player?.get(Transform);
  const drops = ctx.level.drops;
  if (!position || !drops) return null;
  const nav = navFor(ctx.level);
  const feet = position.y - ctx.tuning.height / 2;
  const current = surfaceBelow(nav, position.x, feet);
  const targetX = position.x + ctx.rng.range(-0.5, 0.5);
  let best: { x: number; score: number } | null = null;
  nav.surfaces.forEach((surface, surfaceIndex) => {
    if (current >= 0 && surfaceIndex !== current && !routeStep(nav, current, surfaceIndex)) return;
    const left = Math.max(drops.xMin, surface.x1 + 0.4);
    const right = Math.min(drops.xMax, surface.x2 - 0.4);
    if (left > right) return;
    const clamp = (x: number) => Math.max(left, Math.min(right, x));
    for (const x of [clamp(targetX), clamp(targetX - 1.2), clamp(targetX + 1.2), left, right]) {
      // A clear column now can be intercepted while the gun falls. Keep opening drops outside
      // the full sweep of authored moving platforms above the intended landing surface.
      const intercepted = ctx.level.objects.some((object) => {
        if (object.type !== 'platform.moving') return false;
        const points = object.path ?? [];
        const amplitudeX = points.length > 1 ? Math.abs(points[0]!.x - points[1]!.x) / 2 : 3.5;
        const amplitudeY = points.length > 1 ? Math.abs(points[0]!.y - points[1]!.y) / 2 : 0;
        return (
          Math.abs(x - object.x) <= (object.w ?? 2) / 2 + amplitudeX + 0.3 &&
          object.y + amplitudeY + (object.h ?? 1) / 2 > surface.top + 0.15
        );
      });
      if (intercepted) continue;
      const ground = raycastClosest(
        world,
        x,
        y,
        x,
        ctx.level.bounds.y - 1,
        (h) => h.kind !== 'solid' && h.kind !== 'prop',
      );
      if (
        !ground ||
        ground.fixture.getBody().getType() !== 'static' ||
        Math.abs(ground.y - surface.top) > 0.15
      )
        continue;
      const score =
        Math.abs(x - position.x) +
        Math.abs(surface.top - feet) * 1.5 +
        (surfaceIndex === current ? 0 : 2);
      if (!best || score < best.score) best = { x, score };
    }
  });
  return (best as { x: number; score: number } | null)?.x ?? null;
}

export function spawner(world: World): void {
  const ctx = getContext(world);
  if (ctx.settings.mode === 'arms-race') return;
  const round = world.get(RoundState);
  const drop = world.get(DropState);
  if (!round || !drop || !ctx.level.drops?.enabled) return;
  if (round.phase !== RoundPhase.Fighting) return;

  let loose = 0;
  world.query(Weapon, Loose).forEach(() => {
    loose += 1;
  });
  drop.looseCount = loose;
  if (ctx.tick < drop.nextDrop) {
    world.set(DropState, drop);
    return;
  }
  if (loose >= looseWeaponLimit(world)) return;

  const selected = world.get(PartyState)?.weaponDef ?? -1;
  const pool = selected >= 0 ? [weaponByIndex(selected)] : partyWeaponPool(ctx.settings);
  if (pool.length === 0) return;
  const weights = pool.map((w) => w.dropWeight);
  const total = weights.reduce((a, b) => a + b, 0);
  let pick = ctx.rng.next() * total;
  let def = pool[0]!;
  for (let i = 0; i < pool.length; i++) {
    pick -= weights[i]!;
    if (pick <= 0) {
      def = pool[i]!;
      break;
    }
  }
  const { bounds } = ctx.level;
  const y = bounds.y + bounds.h + 1.5;
  const { xMin, xMax } = ctx.level.drops;
  // Prefer useful opening pickups; retain the usual ground-column sampler for later drops and
  // arenas whose geometry has no suitable static support (for example, entirely moving stages).
  const inWave = drop.wave > 0;
  const slice = inWave ? (xMax - xMin) / drop.waveSize : xMax - xMin;
  const lo = inWave ? xMin + slice * (drop.waveSize - drop.wave) : xMin;
  const hi = lo + slice;
  // A crate that falls straight into the void is a wasted drop: prefer columns with ground under them.
  let x = inWave ? openingDropX(world, drop.waveSize - drop.wave, y) : null;
  if (x === null) {
    x = ctx.rng.range(lo, hi);
    for (let attempt = 0; attempt < 6; attempt++) {
      const candidate = attempt === 0 ? x : ctx.rng.range(lo, hi);
      const ground = raycastClosest(
        world,
        candidate,
        y,
        candidate,
        bounds.y - 1,
        (h) => h.kind !== 'solid' && h.kind !== 'prop',
      );
      if (ground) {
        x = candidate;
        break;
      }
    }
  }
  spawnWeapon(world, def.id, x, y);
  if (inWave) {
    drop.wave -= 1;
  }
  if (drop.wave > 0) {
    drop.nextDrop = ctx.tick + WAVE_GAP_TICKS;
  } else {
    // More fighters burn through more guns: tighten the cadence as the lobby grows.
    const crowd = 1 + 0.15 * Math.max(0, ctx.players.length - 2);
    const scale = (ctx.level.drops.intervalScale ?? 1) / crowd;
    drop.nextDrop =
      ctx.tick +
      Math.floor(
        ctx.rng.range(ctx.tuning.dropIntervalMinTicks, ctx.tuning.dropIntervalMaxTicks) * scale,
      );
  }
  world.set(DropState, drop);
}
